import { AuthorizeFrontNotice } from '@/components/AuthorizeFrontNotice';
import { AuthorizeUnderlyingInboxNotice } from '@/components/AuthorizeUnderlyingInboxNotice';
import { useFrontContext } from '@/contexts/useFrontPluginContext';
import { Text } from '@shared/components';
import { AuthorizeGmailNotice } from '@shared/components/authorizeInstructions/AuthorizeGmailNotice';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import {
  AuthorizedIntegrationContext,
  AuthorizedIntegrationContextType,
} from '@shared/contexts/pluginContexts/AuthorizedIntegrationContext';
import { useLazyHasAuthorizedIntegration } from '@shared/contexts/pluginContexts/useHasAuthorizedIntegration';
import {
  AuthTokenType,
  IntegrationCredentialsType,
  UsageIndicator,
} from '@shared/generated/graphql';
import { useOrganizationHasCredential } from '@shared/graphql/hooks/organization/useOrganizationHasCredential';
import { FrontProvider } from 'clerk_common/types/organization';
import { useEffect, useState } from 'react';

type FrontAuthorizatedIntegrationProviderProps = {
  children: JSX.Element;
};

const getIntegrationType = (
  provider: FrontProvider | null
): AuthTokenType | null => {
  switch (provider) {
    case 'OUTLOOK':
      return AuthTokenType.MICROSOFT_GRAPH_API;
    case 'GMAIL':
      return AuthTokenType.GMAIL_API;
    default:
      return null;
  }
};

export const FrontAuthorizedIntegrationProvider = ({
  children,
}: FrontAuthorizatedIntegrationProviderProps) => {
  const { defaultOrgId, frontEmailProvider } = useMeContext();
  const { frontUser } = useFrontContext();
  const [emailProviderChecked, setEmailProviderChecked] = useState(false);
  const integrationType = getIntegrationType(frontEmailProvider);

  const { getAuthorizedServiceQuery, ...emailProviderAuthorization } =
    useLazyHasAuthorizedIntegration();

  useEffect(() => {
    if (integrationType) {
      getAuthorizedServiceQuery({
        variables: {
          type: integrationType,
          externalUserId: frontUser.id,
        },
      });
      setEmailProviderChecked(true);
    }
  }, [integrationType, getAuthorizedServiceQuery, frontUser.id]);

  const frontAuthorization = useOrganizationHasCredential({
    organizationId: defaultOrgId ?? '',
    type: IntegrationCredentialsType.FRONT,
    usageIndicator: UsageIndicator.PRODUCTION,
  });

  const refetchBoth = async () => {
    await Promise.all([
      integrationType ? emailProviderAuthorization.refetch() : undefined,
      frontAuthorization.refetch(),
    ]);
  };

  const isEmailProviderAuthorizationLoading =
    integrationType !== null &&
    (!emailProviderChecked || emailProviderAuthorization.loading);

  const value: AuthorizedIntegrationContextType = {
    authorizedEmail: emailProviderAuthorization.authorizedEmail,
    authorized:
      emailProviderAuthorization.authorized &&
      (frontAuthorization.hasCredential ?? false),
    loading: isEmailProviderAuthorizationLoading || frontAuthorization.loading,
    refetch: refetchBoth,
    integrationName: IntegrationCredentialsType.FRONT,
    renderAuthInstructions: () => (
      <AuthorizeFrontIntegrationsNotice
        frontIsAuthorized={frontAuthorization.hasCredential ?? false}
        emailIsAuthorized={emailProviderAuthorization.authorized}
        emailProvider={frontEmailProvider}
        refetch={refetchBoth}
      />
    ),
  };

  return (
    <AuthorizedIntegrationContext.Provider value={value}>
      {children}
    </AuthorizedIntegrationContext.Provider>
  );
};

type AuthorizeFrontIntegrationsNoticeProps = {
  frontIsAuthorized: boolean;
  emailIsAuthorized: boolean;
  emailProvider: FrontProvider | null;
  refetch: () => void;
};
export const AuthorizeFrontIntegrationsNotice = (
  p: AuthorizeFrontIntegrationsNoticeProps
) => {
  if (!p.frontIsAuthorized) return <AuthorizeFrontNotice />;
  if (!p.emailIsAuthorized) {
    if (p.emailProvider === 'OUTLOOK') {
      return <AuthorizeUnderlyingInboxNotice />;
    } else if (p.emailProvider === 'GMAIL') {
      return <AuthorizeGmailNotice />;
    } else {
      return (
        <div className="p-2">
          <Text type="body-sm" isHeavy className="text-center text-gray-600">
            We cannot determine your organization's email provider. Please
            contact support, support@vooma.ai.
          </Text>
        </div>
      );
    }
  }
  return null;
};
